import type { User } from 'types';
import type { AvatarColour } from 'helpers/user-identity';
import type { ResetSignInState } from 'definitions/password-reset';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { refreshClientToken, useClient } from 'hooks/useClient';
import { useGlobal } from 'hooks/useGlobal';
import { usePasskeys } from './usePasskeys';
import { config } from 'config';

type UseAuthProps = { authUrl?: string; apiUrl?: string } | undefined;
export type UserWithToken = User & { token: string };
/** `accountType` (a COMPANY_TYPES value) is present only on the call that activated the account. */
export type VerifyResult = { ok: boolean; accountType?: string | null };
/** `signIn` says whether the new password can be used yet: a reset never changes the account's status. */
export type ResetPasswordResult = { ok: boolean; signIn?: ResetSignInState };

export const useAuth = (props: UseAuthProps = {}) => {
  const { authUrl = config.AUTH_URL, apiUrl = config.API_URL } = props;
  const queryClient = useQueryClient();
  const [user, setUser, refreshUser] = useGlobal<UserWithToken | null>('CURRENT_USER', null);
  const authClient = useClient(authUrl);
  const apiClient = useClient(apiUrl);
  const passKeyManager = usePasskeys();

  const { isLoading } = useQuery<UserWithToken | null>({
    queryKey: ['CURRENT_USER'],
    queryFn: async () => {
      const token =
        window.pertentoBearerToken ||
        document.body.dataset['pertentoBearerToken'] ||
        document.body.dataset['bearerToken'] ||
        new URL(window.location.href).searchParams.get('pertentoToken') ||
        localStorage.getItem('PERTENTO_EDITOR_AUTH_TOKEN') ||
        localStorage.getItem('BEARER_TOKEN') ||
        sessionStorage.getItem('BEARER_TOKEN');
      if (!token) {
        setUser(null);
        return null;
      }

      try {
        const dbUser = (await authClient.get('/me')) as UserWithToken;
        setUser({ ...dbUser, token });
        return { ...dbUser, token } as UserWithToken;
      } catch (err) {
        return null;
      }
    },
  });

  const signin = async ({ email, password }: { email: string; password: string }) => {
    localStorage.removeItem('BEARER_TOKEN');
    const data = await authClient.post('/signin', { email, password });

    const { user, token } = data;

    if (!token && user.passkeys > 0) {
      await passKeyManager.signinWithPasskey(user);
      return;
    }

    localStorage.setItem('BEARER_TOKEN', data.token);
    // Before the refetch: it asks /me straight away, and the client reads the token on render.
    refreshClientToken();
    queryClient.invalidateQueries({ queryKey: ['CURRENT_USER'] });
    return data.token;
  };

  const signout = async () => {
    // Invalidate token on server (best-effort, don't block on failure)
    try {
      await authClient.post('/signout');
    } catch {
      // Token may already be invalid, continue with local cleanup
    }
    setUser(null);
    localStorage.removeItem('BEARER_TOKEN');
    localStorage.removeItem('CURRENT_USER');
    // Or requests until the next render would still carry the signed-out token.
    refreshClientToken();
  };

  const signup = async (newUser: SignupUserForm) => {
    return await authClient.post('/signup', newUser);
  };

  const resetPassword = async (payload: ResetPasswordPayload): Promise<ResetPasswordResult> => {
    return (await authClient.post('/reset-password', payload)) as ResetPasswordResult;
  };

  const verify = async (verificationCode: string): Promise<VerifyResult> => {
    return (await authClient.post('/verify', { verificationCode })) as VerifyResult;
  };

  const updateUser = async (payload: UpdateUserPayload) => {
    await apiClient.put('/users', payload);
    localStorage.removeItem('CURRENT_USER');
    refreshUser();
    queryClient.invalidateQueries({ queryKey: ['CURRENT_USER'] });
  };

  const changePassword = async (payload: ChangePasswordPayload) => {
    await authClient.post('/change-password', payload);
  };

  const forgotPassword = async (payload: ForgotPasswordPayload) => {
    await authClient.post('/forgot-password', payload);
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    refreshUser,
    signin,
    signout,
    signup,
    verify,
    resetPassword,
    updateUser,
    changePassword,
    forgotPassword,
    ...passKeyManager,
  };
};

export type SignupUserForm = {
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  confirmPassword?: string;
  companyName?: string;
  companyType?: string;
};

/**
 * Only the fields being changed; `avatarColour: null` goes back to automatic, and
 * `avatar: null` removes the photo. A photo is an image data URL (see isAvatarDataUrl).
 */
export type UpdateUserPayload = {
  firstName?: string;
  lastName?: string;
  avatar?: string | null;
  avatarColour?: AvatarColour | null;
};

export type ResetPasswordPayload = {
  password: string;
  verificationCode: string;
};

export type ChangePasswordPayload = {
  password: string;
  newPassword: string;
  confirmPassword: string;
};

export type ForgotPasswordPayload = { email: string };
